import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Select, Tag, Tooltip } from 'antd';
import { CaretDownOutlined, CaretRightOutlined, PlusOutlined } from '@ant-design/icons';

import { validEmail } from '../../../../global/validation';
import { captureAnalytics } from '../../../../actions/preload';
import { useTableScrollDismiss } from '../../../../components/inbox/TableScrollContext';
import { confirmTeamReassign, teamReassignNeedsConfirm } from '../../../../components/teams/teamPicker';
import { isExpandableReferralPrimaryRow } from './groupReferralRows';

// Payload for the shared "Add label to record" modal (ModalRoot key
// ADD_LABEL_TO_RECORD). Co-located with AddLabelCell so the ReferralManagement
// page and the redesigned Inbox open the modal with an identical shape and can't
// drift. selectedLabelsRecordViewer is a superset field the received-records
// flow ignores, so it is safe for both surfaces.
export const buildOpenLabelModalPayload = (record) => ({
  type: 'ADD_LABEL_TO_RECORD',
  data: {
    reportAiDetails: {
      isLabelEdited: record.is_label_edited,
      hasWrongLabelingBeenReported: record.has_wrong_labeling_been_reported,
      aiStatus: record.ai_status,
    },
    record: record.send_token,
    tag: record.tag,
    receiverType: record.receiver_type,
    selectedLabelsRecordViewer: record.labels || [],
  },
});

const renderBaseTitle = (titleColumn, text, record, index) => {
  if (typeof titleColumn.render === 'function') {
    return titleColumn.render(text, record, index);
  }

  return text;
};

export const ReferralTitleCell = ({
  titleColumn,
  text,
  record,
  index,
  expandedReferralEntitySlugs,
  onToggleReferralEntity,
}) => {
  const slug = record?.referral_entity_slug;
  const expandable = isExpandableReferralPrimaryRow(record);
  const expanded = slug ? expandedReferralEntitySlugs.has(slug) : false;
  const baseTitle = renderBaseTitle(titleColumn, text, record, index);

  const className = [
    'referral-linked-title',
    record?.isReferralChildRow ? 'referral-linked-title--child' : null,
  ].filter(Boolean).join(' ');

  return (
    <span className={className}>
      {expandable && (
        <button
          type="button"
          className="referral-linked-title__toggle"
          aria-label={expanded ? 'Collapse linked referral rows' : 'Expand linked referral rows'}
          aria-expanded={expanded}
          data-testid={`referral-row-toggle-${slug}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggleReferralEntity(slug);
          }}
        >
          {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
        </button>
      )}
      <span className="referral-linked-title__content">
        {baseTitle}
      </span>
    </span>
  );
};

ReferralTitleCell.propTypes = {
  titleColumn: PropTypes.object.isRequired,
  text: PropTypes.node,
  record: PropTypes.object.isRequired,
  index: PropTypes.number,
  expandedReferralEntitySlugs: PropTypes.instanceOf(Set).isRequired,
  onToggleReferralEntity: PropTypes.func.isRequired,
};

ReferralTitleCell.defaultProps = {
  text: null,
  index: 0,
};

// Grouped linked children hide their Status & Due Date control in the redesign
// because status changes apply to the referral group via the primary row. We
// gate on referralParentFileToken (set by buildChildRow when the primary is on
// the same page) rather than is_primary_referral_record so that orphan linked
// rows — children whose primary is on a different page — render as flat
// top-level rows with their own status controls. The strip is applied here
// (redesign-only) so the legacy ReferralManagement page keeps inline controls.
const STATUS_AND_DUE_DATE_TITLE = 'Status and Due Date';
const LABELS_TITLE = 'Labels';
const TEAMS_TITLE = 'Team';
const NO_PAGES_TITLE = 'No. Pages';
// Sentinel option value for the "No team" (clear) entry in the inline picker.
const NO_TEAM_VALUE = '__no_team__';

// Keep the menu outside the table's vertical clip. The owning cell closes its
// controlled editing state when its InboxTable reports a user scroll gesture.
export const getTeamPickerPopupContainer = () => document.body;

// Read-only team chip, color-coded by the team (mirrors the label chip style).
const teamChip = (team) => (
  <Tag
    className="referral-row-team"
    style={{ backgroundColor: team.color, color: team.is_dark_text ? 'black' : 'white', margin: 0 }}
  >
    {team.name}
  </Tag>
);

// Teams column cell (D2/D4). Read-only chip when the row can't be edited; an
// affordance (chip or "+ Team") that opens an INLINE single-select on click when
// record.can_edit_team is true. Not a modal, not a permanent dropdown: the cell
// looks like a value and drops a Select open on click, mirroring AddLabelCell's
// affordance and LabelPickerTray's chip styling (single-select, no tray/slots).
export const TeamPickerCell = ({ record, teams, onSetTeam }) => {
  const [editing, setEditing] = useState(false);
  useTableScrollDismiss(() => setEditing(false));
  const canEdit = record?.can_edit_team === true;
  const team = record?.team || null;

  if (!editing) {
    if (!canEdit) {
      return team ? teamChip(team) : null;
    }
    return (
      <button
        type="button"
        className="referral-team-cell"
        aria-label={team ? `Change team (currently ${team.name})` : 'Set team'}
        title={team ? 'Change team' : 'Set team'}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setEditing(true);
        }}
      >
        {team ? teamChip(team) : (
          <span className="referral-team-cell__add">
            <PlusOutlined className="referral-team-cell__icon" />
            {' Team'}
          </span>
        )}
      </button>
    );
  }

  const options = [
    { value: NO_TEAM_VALUE, name: 'No team', label: <span className="referral-team-cell__clear">No team</span> },
    ...teams.map((teamOption) => ({
      value: teamOption.id,
      name: teamOption.name,
      label: teamChip(teamOption),
    })),
  ];

  const handleChange = (value) => {
    setEditing(false);
    const nextSlug = value === NO_TEAM_VALUE ? null : value;
    const currentSlug = team ? team.id : null;
    if (nextSlug === currentSlug) return;
    const proceed = () => {
      captureAnalytics('record_team_set', { cleared: nextSlug === null });
      onSetTeam(record, nextSlug);
    };
    // Only a real cross-team reassignment that costs the current user visibility
    // needs a confirm; the common case stays synchronous (see confirmTeamReassign).
    if (teamReassignNeedsConfirm({ record, nextTeamSlug: nextSlug })) {
      confirmTeamReassign({ record, nextTeamSlug: nextSlug }).then((confirmed) => {
        if (confirmed) proceed();
      });
    } else {
      proceed();
    }
  };

  return (
    <Select
      className="referral-team-cell__select"
      size="small"
      autoFocus
      defaultOpen
      showSearch
      optionLabelProp="label"
      placeholder="Select team"
      value={team ? team.id : undefined}
      options={options}
      onChange={handleChange}
      onBlur={() => setEditing(false)}
      onClick={(event) => event.stopPropagation()}
      filterOption={(input, option) => (
        String(option?.name || '').toLowerCase().includes(input.toLowerCase())
      )}
      getPopupContainer={getTeamPickerPopupContainer}
      style={{ width: '100%' }}
    />
  );
};

TeamPickerCell.propTypes = {
  record: PropTypes.object.isRequired,
  teams: PropTypes.array,
  onSetTeam: PropTypes.func,
};

TeamPickerCell.defaultProps = {
  teams: [],
  onSetTeam: () => {},
};

const buildTeamColumn = ({ teams, onSetRecordTeam }) => ({
  title: TEAMS_TITLE,
  dataIndex: 'team',
  // Reserve a stable footprint so opening the inline picker doesn't grow the
  // column (and overflow the table). Matches the collapsed affordance and the
  // open Select's width in shared RecordsRedesign styles (.referral-team-cell*).
  width: 150,
  render: (text, record) => (
    <TeamPickerCell record={record} teams={teams} onSetTeam={onSetRecordTeam} />
  ),
});

// Inserts the interactive Teams column immediately to the right of the No. Pages
// column (falling back to after Labels, then the end). Only called when the
// `teams` flag is on, so flag-off column output is byte-identical.
const injectTeamColumn = (columns, teamOptions) => {
  const teamColumn = buildTeamColumn(teamOptions);
  const pagesIndex = columns.findIndex((column) => column?.title === NO_PAGES_TITLE);
  const anchorIndex = pagesIndex !== -1
    ? pagesIndex
    : columns.findIndex((column) => column?.title === LABELS_TITLE);
  if (anchorIndex === -1) return [...columns, teamColumn];
  const next = [...columns];
  next.splice(anchorIndex + 1, 0, teamColumn);
  return next;
};

const isGroupedChildRow = (record) => record?.referralParentFileToken != null;
const buildLegacyDepartmentNames = (departments) => new Set(
  departments.flatMap((department) => [
    department?.name,
    department?.slug,
  ]).filter(Boolean),
);

const AddLabelCell = ({ record, onOpenLabelModal }) => (
  <button
    type="button"
    className="referral-add-label-cell"
    aria-label="Add label"
    title="Add label"
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onOpenLabelModal(record);
    }}
  >
    <PlusOutlined className="referral-add-label-cell__icon" />
  </button>
);

AddLabelCell.propTypes = {
  record: PropTypes.object.isRequired,
  onOpenLabelModal: PropTypes.func.isRequired,
};

// Row label chips. Clicking one asks the page to add that label id to the
// active filter. The legacy free-text `tag` mirrors the shared column's render
// and stays non-clickable (it isn't a real, filterable label).
const RowLabelsCell = ({ record, legacyDepartmentNames, onAddLabelFilter }) => {
  const { labels, tag } = record;
  return (
    <>
      {labels && labels.map((label) => (
        <Tag
          key={label.id}
          className="referral-row-label"
          title={`Filter by “${label.name}”`}
          style={{ backgroundColor: label.color, color: label.is_dark_text ? 'black' : 'white' }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onAddLabelFilter(label.id);
          }}
        >
          {label.name}
        </Tag>
      ))}
      {tag && !validEmail(tag) && !legacyDepartmentNames.has(tag) && (
        <Tooltip
          title={(
            <>
              This is an old tag that cannot be used as a label. Please go here
              <Link to="/app/user?tab=manage_labels"> Manage Labels </Link>
              to add a new label to your library
            </>
          )}
        >
          <Tag style={{ color: 'rgb(106, 27, 154)', backgroundColor: 'white', border: '1px solid rgb(106, 27, 154)' }}>
            {tag}
          </Tag>
        </Tooltip>
      )}
    </>
  );
};

RowLabelsCell.propTypes = {
  record: PropTypes.object.isRequired,
  legacyDepartmentNames: PropTypes.instanceOf(Set).isRequired,
  onAddLabelFilter: PropTypes.func.isRequired,
};

// On the redesign, an unlabeled row shows a small "+" affordance (large hit
// area, small icon so it doesn't read as a label) that opens the label modal.
// Labeled rows render clickable chips that add the label to the filter.
const wrapLabelsColumn = (column, {
  legacyDepartmentNames,
  onAddLabelFilter,
  onOpenLabelModal,
}) => ({
  ...column,
  render: (text, record) => {
    const hasLabels = Array.isArray(record?.labels) && record.labels.length > 0;
    const hasLegacyTag = Boolean(record?.tag);
    if (!hasLabels && !hasLegacyTag) {
      return <AddLabelCell record={record} onOpenLabelModal={onOpenLabelModal} />;
    }
    return (
      <RowLabelsCell
        record={record}
        legacyDepartmentNames={legacyDepartmentNames}
        onAddLabelFilter={onAddLabelFilter}
      />
    );
  },
});

const wrapStatusAndDueDateColumn = (column) => {
  const baseRender = typeof column.render === 'function' ? column.render : null;
  return {
    ...column,
    render: (text, record, index) => {
      if (isGroupedChildRow(record)) return null;
      if (!baseRender) return text;
      return baseRender(text, record, index);
    },
  };
};

export const buildReferralColumns = (
  baseColumns,
  {
    departments = [],
    expandedReferralEntitySlugs,
    onAddLabelFilter = () => {},
    onOpenLabelModal = () => {},
    onToggleReferralEntity,
    teamsEnabled = false,
    teams = [],
    onSetRecordTeam = () => {},
  },
) => {
  const [titleColumn, ...remainingColumns] = baseColumns;
  if (!titleColumn) return baseColumns;
  const legacyDepartmentNames = buildLegacyDepartmentNames(departments);

  const columns = [
    {
      ...titleColumn,
      render: (text, record, index) => (
        <ReferralTitleCell
          titleColumn={titleColumn}
          text={text}
          record={record}
          index={index}
          expandedReferralEntitySlugs={expandedReferralEntitySlugs}
          onToggleReferralEntity={onToggleReferralEntity}
        />
      ),
    },
    ...remainingColumns.map((column) => {
      if (column?.title === STATUS_AND_DUE_DATE_TITLE) return wrapStatusAndDueDateColumn(column);
      if (column?.title === LABELS_TITLE) {
        return wrapLabelsColumn(column, {
          legacyDepartmentNames,
          onAddLabelFilter,
          onOpenLabelModal,
        });
      }
      return column;
    }),
  ];

  if (teamsEnabled) return injectTeamColumn(columns, { teams, onSetRecordTeam });
  return columns;
};

// receiver_types that Api::V1::ReceiversController#labels can actually attach a
// label to (received_fax / email_attachment / receiver branches, plus the
// JotformSubmission else-branch). The received inbox also surfaces reply rows
// (receiver_type 'reply'), which have no labels association — the endpoint would
// look a reply up as a JotformSubmission and 404 — so they must not get the "+".
// ReferralManagement never sees replies (referral queries exclude them), which
// is why this only matters for the inbox.
const LABELABLE_RECEIVER_TYPES = new Set([
  'received_fax',
  'email_attachment',
  'receiver',
  'jotform_submission',
]);

// Inbox parity for the empty-labels "+" affordance. The redesigned Inbox has no
// referral grouping (no expandable title caret, no Status & Due Date child
// stripping), so it can't reuse buildReferralColumns wholesale. This wraps only
// the Labels column: unlabeled, labelable rows get the same AddLabelCell "+" that
// opens the add-label modal, while labeled rows (and rows whose receiver_type the
// add-label endpoint can't handle) fall through to the shared column's existing
// chip/legacy-tag render. We intentionally do not layer on the Referrals page's
// clickable-chip label filtering here.
export const buildInboxLabelColumns = (baseColumns, {
  onOpenLabelModal = () => {},
  teamsEnabled = false,
  teams = [],
  onSetRecordTeam = () => {},
} = {}) => {
  const columns = baseColumns.map((column) => {
    if (column?.title !== LABELS_TITLE) return column;
    const baseRender = typeof column.render === 'function' ? column.render : null;
    return {
      ...column,
      render: (text, record, index) => {
        const hasLabels = Array.isArray(record?.labels) && record.labels.length > 0;
        const hasLegacyTag = Boolean(record?.tag);
        const isLabelable = LABELABLE_RECEIVER_TYPES.has(record?.receiver_type);
        if (isLabelable && !hasLabels && !hasLegacyTag) {
          return <AddLabelCell record={record} onOpenLabelModal={onOpenLabelModal} />;
        }
        return baseRender ? baseRender(text, record, index) : text;
      },
    };
  });

  if (teamsEnabled) return injectTeamColumn(columns, { teams, onSetRecordTeam });
  return columns;
};
